import { ExternalLink, Images } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "~/components/ui";
import { useEvidenceCitation } from "~/features/changeRequests/hooks";

interface EvidenceCitationProps {
  evidenceId: string;
}

/** One evidence citation in the change-request Source Info: evidence + reference links and the
 *  citation, with a button to open the cited reference-file page (V1 EvidenceCitation parity). */
export function EvidenceCitation({ evidenceId }: EvidenceCitationProps) {
  const { t } = useTranslation();
  const { data } = useEvidenceCitation(evidenceId);
  const evidence = data.evidences[0];

  if (!evidence) {
    return <span className="break-all font-mono text-12-regular text-fg-3">{evidenceId}</span>;
  }

  const reference = evidence.reference;
  const file = reference?.referenceFiles[0] ?? null;
  const page = (evidence.citation as { page?: unknown } | null)?.page;
  const slidesUrl =
    reference && file
      ? `/reference-files/${reference.id}/${file.id}${page == null ? "" : `?folio=${String(page)}`}`
      : null;

  return (
    <div className="divide-y divide-dashed divide-border-default rounded-[var(--radius-sm)] border border-border-default">
      <CitationRow label={t("model.evidence.modelName")}>
        <span className="inline-flex items-center gap-1.5">
          <span className="break-all font-mono text-12-regular text-fg-1">{evidence.id}</span>
          <Link
            to={`/evidences/${evidence.id}`}
            className="shrink-0 text-fg-link hover:text-primary-700"
            aria-label={t("ui.view")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </span>
      </CitationRow>
      {reference && (
        <CitationRow label={t("model.reference.modelName")}>
          <span className="inline-flex items-center gap-1.5">
            <span className="text-fg-1">{reference.title}</span>
            <Link
              to={`/references/${reference.id}`}
              className="shrink-0 text-fg-link hover:text-primary-700"
              aria-label={t("ui.view")}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </span>
        </CitationRow>
      )}
      <CitationRow label={t("model.evidence.citation")}>
        {slidesUrl && file?.imagesCreatedAt ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              window.open(slidesUrl, "_blank", "location=yes,height=700,width=520,scrollbars=yes,status=yes")
            }
          >
            <Images />
            <span>{t("ui.view")}</span>
          </Button>
        ) : (
          <CitationValue value={evidence.citation} />
        )}
      </CitationRow>
    </div>
  );
}

function CitationRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[minmax(5rem,7rem)_1fr] items-center gap-3 px-3 py-1.5">
      <span className="text-fg-3">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function CitationValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || (typeof value === "object" && Object.keys(value).length === 0)) {
    return <span className="text-fg-3">—</span>;
  }
  if (typeof value === "object") {
    return (
      <dl className="flex flex-col gap-0.5">
        {Object.entries(value as Record<string, unknown>).map(([key, val]) => (
          <div key={key} className="flex gap-2">
            <dt className="font-mono text-fg-3">{key}:</dt>
            <dd className="min-w-0 break-words text-fg-1">
              {typeof val === "object" ? JSON.stringify(val) : String(val)}
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="break-words text-fg-1">{String(value)}</span>;
}
